import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import {
  ConditionsSection,
  ContratSection,
  EcheancierSection,
  GrilleSection,
  HistoriqueSection,
  ProcessusAchatSection,
} from './DossierSections';
import { messageErreur, useSkipcarPlusDossier } from './skipcarPlusApi';
import { PHASES, aujourdhuiIso, avancementContrat, dateFr, euros, pourcent } from './skipcarPlusFormat';
import { CheminRetour, lireRetour } from './skipcarPlusRetour';

import './skipcarPlusTokens.css';
import './skipcarPlusLayout.css';

/**
 * Ligne de vie d'un contrat Skipcar+ (lots 1 à 4) : même écran pour Skipcar
 * (vue Dieu, avec les outils internes) et pour l'agence du dossier.
 * Styles : tokens de François, confinés sous `.sk-plus`.
 */
const SkipcarPlusDossierPage: React.FC = () => {
  const { dealId } = useParams<{ dealId: string }>();
  const { data: dossier, isLoading, error, refetch } = useSkipcarPlusDossier(dealId);
  const retourDemande = lireRetour(useLocation().state);

  if (isLoading) {
    return (
      <div className="sk-plus sp-page">
        <p className="sp-muted">Chargement du contrat…</p>
      </div>
    );
  }

  if (error || !dossier) {
    return (
      <div className="sk-plus sp-page">
        <p className="sp-error">{messageErreur(error)}</p>
        <Link className="sp-back" to={`/skipcar-plus/${dealId}/simulation`}>
          Revenir à la simulation
        </Link>
      </div>
    );
  }

  const rangPhase = PHASES.findIndex((phase) => phase.cle === dossier.phase);
  const { montants, vehicule } = dossier;
  const couverture = montants.couvertureCentimes ?? 0;
  const coutMaximum = dossier.simulation.length > 0 ? dossier.simulation[dossier.simulation.length - 1].cumulHtCentimes : 0;
  const avancement = avancementContrat(dossier.j0.date, dossier.grille.taux, dossier.echeancier, aujourdhuiIso());
  const plaque = vehicule.immatriculation ?? `Dossier ${dossier.dealId}`;
  // Le retour suit le chemin réel : la liste, ou l'écran d'où l'on vient (jamais un parent codé en dur).
  const retour: CheminRetour =
    retourDemande ??
    (dossier.vueDieu
      ? { vers: '/skipcar-plus', libelle: 'Skipcar+' }
      : { vers: `/skipcar-plus/${dossier.dealId}/simulation`, libelle: 'Simulation' });
  const depuisCeDossier: CheminRetour = { vers: `/skipcar-plus/${dossier.dealId}`, libelle: plaque };

  return (
    <div className="sk-plus sp-page">
      <div className="sp-head">
        <div>
          <Link className="sp-back" to={retour.vers}>
            ‹ {retour.libelle}
          </Link>
          <h1 className="sk-display">{plaque}</h1>
          <div className="sp-sub">
            {dossier.agence.nom ?? 'Agence inconnue'} · {[vehicule.marque, vehicule.modele].filter(Boolean).join(' ')}
          </div>
        </div>
        <div className="sp-row">
          <Link className="sk-btn sk-btn-secondary" to={`/skipcar-plus/${dossier.dealId}/simulation`} state={depuisCeDossier}>
            Simulation
          </Link>
          {dossier.vueDieu && (
            <Link className="sk-btn sk-btn-ghost" to="/skipcar-plus/profils" state={depuisCeDossier}>
              Profils de taux
            </Link>
          )}
        </div>
      </div>

      <div className="sp-stepper">
        {PHASES.map((phase, index) => (
          <div
            key={phase.cle}
            className={`sp-step${index < rangPhase ? ' is-done' : ''}${index === rangPhase ? ' is-current' : ''}`}
          >
            {phase.libelle}
          </div>
        ))}
      </div>

      <div className="sp-stack">
        <section className="sk-card sp-indicators">
          <div className="sp-indicator">
            <div className="sk-kicker">Couverture Skipcar</div>
            <div className="sp-indicator-value">{euros(couverture, 0)}</div>
            <div className="sp-muted">
              participation agence {euros(montants.participationHtCentimes, 0)} HT
            </div>
          </div>
          <div className="sp-indicator">
            <div className="sk-kicker">Semaine</div>
            <div className="sp-indicator-value">
              {avancement.etat === 'avant_j0' ? 'Avant J0' : `${avancement.semaine}/52`}
            </div>
            <div className="sp-muted">
              {avancement.etat === 'terme' ? 'contrat arrivé à son terme' : avancement.etat === 'portage' ? 'en portage' : 'le compteur part au J0'}
            </div>
          </div>
          <div className="sp-indicator">
            <div className="sk-kicker">Palier actif</div>
            <div className="sp-indicator-value">{pourcent(avancement.taux)}</div>
            <div className="sp-muted">palier {avancement.palier} · par semaine, sur la couverture</div>
          </div>
          <div className="sp-indicator">
            <div className="sk-kicker">Prochain prélèvement</div>
            <div className="sp-indicator-value">
              {avancement.prochaine ? euros(avancement.prochaine.montantTtcCentimes) : '—'}
            </div>
            <div className="sp-muted">
              {avancement.prochaine
                ? `TTC le ${dateFr(avancement.prochaine.datePrelevement)}`
                : avancement.etat === 'terme'
                  ? 'plus aucun prélèvement'
                  : 'dès que le J0 est connu'}
            </div>
          </div>
          <div className="sp-indicator">
            <div className="sk-kicker">J0</div>
            <div className="sp-indicator-value">{dossier.j0.date ? dateFr(dossier.j0.date) : 'À venir'}</div>
            <div className="sp-muted">{dossier.j0.estime ? 'estimé' : 'date d’achat'}</div>
          </div>
          <div className="sp-indicator">
            <div className="sk-kicker">Conditions</div>
            <div className="sp-indicator-value">
              {dossier.bilanConditions.validees}/{dossier.bilanConditions.total}
            </div>
            <div className="sp-muted">avant le premier prélèvement</div>
          </div>
          {dossier.vueDieu && (
            <div className="sp-indicator">
              <div className="sk-kicker">Coût sur 52 semaines</div>
              <div className="sp-indicator-value">{euros(coutMaximum, 0)}</div>
              <div className="sp-muted">exposition interne, pas un argument commercial</div>
            </div>
          )}
        </section>

        <div className="sp-grid-2">
          <ContratSection dossier={dossier} rafraichir={refetch} />
          <GrilleSection dossier={dossier} rafraichir={refetch} />
        </div>
        <ConditionsSection dossier={dossier} rafraichir={refetch} />
        <ProcessusAchatSection dossier={dossier} />
        <EcheancierSection dossier={dossier} />
        <HistoriqueSection dossier={dossier} />
      </div>
    </div>
  );
};

export default SkipcarPlusDossierPage;
